import React, { useEffect, useState } from "react";
import { useParams, Link, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { db } from "../config/firebaseConfig";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { useTranslation } from "react-i18next";

const AuthorsList = () => {
  const { t } = useTranslation();
  const [authors, setAuthors] = useState([]);
  const [filter, setFilter] = useState("*");
  const [filterLetters, setFilterLetters] = useState([]);

  useEffect(() => {
    const fetchAuthors = async () => {
      const authorsQuery = query(
        collection(db, "authors"),
        where("status", "==", 1),
        orderBy("name", "asc")
      );
      const authorsSnapshot = await getDocs(authorsQuery);
      const authorsData = authorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAuthors(authorsData);

      const uniqueLetters = [
        ...new Set(authorsData.map((author) => author.name[0].toLowerCase())),
      ].sort();
      setFilterLetters(uniqueLetters);
    };

    fetchAuthors();
  }, []);

  const filteredAuthors =
    filter === "*"
      ? authors
      : authors.filter(
          (author) => author.name[0].toLowerCase() === filter.replace(".", "")
        );

  return (
    <>
      <Helmet>
        <title>{t("authorsPage.metaTitle")}</title>
        <link rel="canonical" href="https://agency.littlehibba.com/authors" />
        <meta name="description" content={t("authorsPage.metaDescription")} />
        <meta property="og:title" content={t("authorsPage.ogTitle")} />
        <meta property="og:description" content={t("authorsPage.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <section className="auth-svc-hero-modern auth-section py-5" style={{ backgroundColor: "var(--background-color)" }}>
        <div className="container text-center">
          <h1 className="auth-title mb-3" style={{ color: "var(--heading-color)", fontFamily: "var(--bs-font-sans-serif)", fontWeight: 700 }}>
            {t("authorsPage.title")}
          </h1>
          <p className="auth-description mb-5" style={{ color: "var(--default-color)", fontFamily: "var(--bs-font-sans-serif)" }}>
            {t("authorsPage.description")}
          </p>
          <div className="auth-filter-group mb-5 d-flex justify-content-center flex-wrap gap-2">
            <button
              className={`auth-filter-btn btn ${filter === "*" ? "auth-filter-active" : ""}`}
              onClick={() => setFilter("*")}
            >
              {t("authorsPage.allAuthors")}
            </button>
            {filterLetters.map((letter) => (
              <button
                key={letter}
                className={`auth-filter-btn btn ${filter === `.${letter}` ? "auth-filter-active" : ""}`}
                onClick={() => setFilter(`.${letter}`)}
              >
                {letter.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
            {filteredAuthors.map((author) => (
              <div
                key={author.id}
                className={`col auth-grid-item ${author.name[0].toLowerCase()}`}
              >
                <div className="auth-card card h-100">
                  <Link to={`/authors/${author.id}`}>
                    <img
                      src={
                        author.img
                          ? `images/authors/${author.img}.png`
                          : "images/thumb.jpg"
                      }
                      alt={`${author.name} ${author.surname}`}
                      className="auth-card-img card-img-top"
                      loading="lazy"
                    />
                  </Link>
                  <div className="auth-card-body card-body text-center">
                    <h5 className="auth-card-title card-title" style={{ color: "var(--heading-color)", fontFamily: "var(--bs-font-sans-serif)" }}>
                      {author.name.toUpperCase()} <span style={{ color: "var(--secondary-color)" }}>{author.surname.toUpperCase()}</span>
                    </h5>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const AuthorDetail = () => {
  const { t } = useTranslation();
  const { authorID } = useParams();
  const [author, setAuthor] = useState(null);
  const [works, setWorks] = useState([]);

  useEffect(() => {
    const fetchAuthor = async () => {
      const authorQuery = query(collection(db, "authors"), where("id", "==", authorID));
      const authorSnapshot = await getDocs(authorQuery);
      if (!authorSnapshot.empty) {
        setAuthor(authorSnapshot.docs[0].data());
      }

      const worksQuery = query(collection(db, "authors_works"), where("author_id", "==", authorID));
      const worksSnapshot = await getDocs(worksQuery);
      const worksData = worksSnapshot.docs.map((doc) => doc.data());
      setWorks(worksData);
    };

    fetchAuthor();
  }, [authorID]);

  if (!author) return <div>{t("authorsPage.loading")}</div>;

  return (
    <>
      <Helmet>
        <title>{t("authorsPage.authorMetaTitle", { name: author.name, surname: author.surname })}</title>
        <meta
          name="description"
          content={t("authorsPage.authorMetaDescription", { name: author.name, surname: author.surname })}
        />
        <meta 
          property="og:title" 
          content={t("authorsPage.authorOgTitle", { name: author.name, surname: author.surname })} 
        />
        <meta
          property="og:description"
          content={t("authorsPage.authorOgDescription", { name: author.name, surname: author.surname })}
        />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <section className="auth-detail-section py-5" style={{ backgroundColor: "var(--background-color)" }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="auth-detail-title mb-3" style={{ color: "var(--heading-color)", fontFamily: "var(--bs-font-sans-serif)", fontWeight: 700 }}>
                {author.name.toUpperCase()} <span style={{ color: "var(--secondary-color)" }}>{author.surname.toUpperCase()}</span>
              </h1>
              <p className="auth-detail-bio mb-4" style={{ color: "var(--default-color)", fontFamily: "var(--bs-font-sans-serif)" }}>
                {author.bio}
              </p>
              <a
                href={`https://www.instagram.com/${author.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="auth-detail-social"
                style={{ color: "var(--accent-color)", fontFamily: "var(--bs-font-sans-serif)" }}
              >
                <i className="bi bi-instagram me-2"></i>
                {author.instagram}
              </a>
            </div>
            <div className="col-md-4 text-center">
              <img
                src={`images/authors/${author.img}.png`}
                alt={`${author.name} ${author.surname}`}
                className="auth-detail-img rounded-circle"
                style={{ border: "3px solid var(--accent-color)" }}
              />
            </div>
          </div>
        </div>
      </section>

      {works.length > 0 && (
        <section className="auth-works-section py-5" style={{ backgroundColor: "var(--surface-color)" }}>
          <div className="container">
            <h1 className="auth-works-title mb-5 text-center" style={{ color: "var(--heading-color)", fontFamily: "var(--bs-font-sans-serif)", fontWeight: 700 }}>
              {t("authorsPage.publishedWorks")}
            </h1>
            <div className="row row-cols-1 row-cols-md-2 g-4">
              {works.map((work, index) => (
                <div key={index} className="col auth-work-item">
                  <div className="auth-work-card card h-100">
                    <a href={work.link} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`images/authors/jobs/${work.img}.png`}
                        alt={`Work ${index + 1}`}
                        className="auth-work-img card-img-top"
                        loading="lazy"
                      />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

const Authors = () => {
  return (
    <Routes>
      <Route path="/" element={<AuthorsList />} />
      <Route path="/:authorID" element={<AuthorDetail />} />
    </Routes>
  );
};

export default Authors;